import { IconAlertCircle, IconCheck, IconExternalLink, IconQrCode } from '@myplayer/ui';
import { type ReactElement } from 'react';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardIcon,
  CardRow,
  CardTitle,
} from './components/ui/primitives.js';
import { QR_CHECK_VERIFIER_NOTICE, type QrCheckEvaluation } from './qr-check-helper.js';

function StatusResultBadge(p: {
  readonly ok: boolean;
  readonly testId: string;
  readonly okLabel: string;
  readonly failLabel: string;
  readonly failVariant?: 'invalid' | 'warning';
}): ReactElement {
  return (
    <Badge variant={p.ok ? 'valid' : (p.failVariant ?? 'invalid')} data-testid={p.testId}>
      {p.ok ? <IconCheck /> : <IconAlertCircle />}
      <span>{p.ok ? p.okLabel : p.failLabel}</span>
    </Badge>
  );
}

export function QrCheckResultsCard({
  evaluation,
}: {
  readonly evaluation: QrCheckEvaluation | null;
}): ReactElement {
  if (evaluation === null || !evaluation.frameAnalysis.decoded) {
    return (
      <Card data-testid="qr-check-results-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardIcon>
              <IconQrCode />
            </CardIcon>
            <div>
              <CardTitle>Scan result</CardTitle>
              <p className="mt-0.5 text-[12px] text-fg-secondary">
                Point your phone camera at a printed QR code to check its link and print layout.
              </p>
            </div>
          </div>
          <Badge variant="outline" data-testid="qr-check-waiting-badge">
            Waiting for scan
          </Badge>
        </CardHeader>
        <CardContent className="space-y-3 text-[13px] text-fg-secondary">
          <p data-testid="qr-check-empty-message">
            Hold the printed QR code steady inside the camera guide. As soon as a code is decoded,
            its link, product match, live link check, and rough print warnings appear here.
          </p>
          <p
            data-testid="qr-check-verifier-notice"
            className="rounded-control border border-subtle bg-surface-subtle px-3 py-2 text-[12px] font-medium text-fg-secondary"
          >
            {QR_CHECK_VERIFIER_NOTICE}
          </p>
        </CardContent>
      </Card>
    );
  }

  const {
    frameAnalysis,
    matchedProduct,
    expectedProductLink,
    matchesExpectedLink,
    liveResolution,
  } = evaluation;
  const { warnings, decodedLink } = frameAnalysis;
  const productLabel =
    matchedProduct?.draft?.title ?? matchedProduct?.title ?? matchedProduct?.primaryKey ?? null;

  return (
    <Card data-testid="qr-check-results-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <CardIcon>
            <IconQrCode />
          </CardIcon>
          <div>
            <CardTitle>Scan result</CardTitle>
            <p className="mt-0.5 text-[12px] text-fg-secondary">
              Plain check of your printed QR code
            </p>
          </div>
        </div>
        <StatusResultBadge
          ok={matchesExpectedLink && liveResolution.resolvesToRightLiveLink}
          testId="qr-check-overall-badge"
          okLabel="Link & live route OK"
          failLabel="Check needed"
          failVariant="warning"
        />
      </CardHeader>
      <CardContent className="space-y-4 text-[13px]">
        <div className="divide-y divide-subtle">
          <CardRow label="Decoded link" className="pt-0">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <code
                data-testid="qr-check-decoded-link"
                className="break-all font-mono text-[12px] text-fg"
              >
                {decodedLink}
              </code>
              {decodedLink !== null && /^https?:\/\//i.test(decodedLink) && (
                <a
                  href={decodedLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open decoded link"
                  className="inline-flex items-center text-fg-secondary hover:text-brand"
                >
                  <IconExternalLink />
                </a>
              )}
            </div>
          </CardRow>
          <CardRow label="Expected product link">
            <div className="flex flex-col items-end gap-1">
              <StatusResultBadge
                ok={matchesExpectedLink}
                testId="qr-check-product-match-badge"
                okLabel="Matches product link"
                failLabel="Does not match expected link"
              />
              <span
                data-testid="qr-check-product-match-summary"
                className="text-[12px] text-fg-secondary"
              >
                {evaluation.expectedLinkSummary}
              </span>
              {expectedProductLink !== null && (
                <code
                  data-testid="qr-check-expected-link"
                  className="break-all font-mono text-[11px] text-fg-secondary"
                >
                  Expected: {expectedProductLink}
                </code>
              )}
            </div>
          </CardRow>
          <CardRow label="Live link resolution" className="pb-0">
            <div className="flex flex-col items-end gap-1">
              <StatusResultBadge
                ok={liveResolution.resolvesToRightLiveLink}
                testId="qr-check-live-resolution-badge"
                okLabel="Resolves to right live link"
                failLabel="Live link mismatch"
              />
              <span
                data-testid="qr-check-live-resolution-summary"
                className="text-[12px] text-fg-secondary"
              >
                {liveResolution.summary}
              </span>
              {liveResolution.expectedLiveUrl !== null && (
                <code
                  data-testid="qr-check-expected-live-url"
                  className="break-all font-mono text-[11px] text-fg-secondary"
                >
                  Target: {liveResolution.expectedLiveUrl}
                </code>
              )}
            </div>
          </CardRow>
        </div>

        <div
          data-testid="qr-check-warnings-section"
          className="space-y-2 border-t border-subtle pt-3"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[12px] font-semibold text-fg">
              Rough camera checks {productLabel !== null ? `for ${productLabel}` : ''}
            </span>
            <StatusResultBadge
              ok={warnings.length === 0}
              testId={
                warnings.length === 0
                  ? 'qr-check-no-warnings-badge'
                  : 'qr-check-warning-count-badge'
              }
              okLabel="No rough warnings"
              failLabel={`${warnings.length} ${warnings.length === 1 ? 'warning' : 'warnings'}`}
              failVariant="warning"
            />
          </div>
          {warnings.length === 0 ? (
            <p
              data-testid="qr-check-warnings-clean"
              className="rounded-control border border-status-valid-border bg-status-valid-bg px-3 py-2 text-[12px] text-status-valid"
            >
              Quiet zone, symbol size in frame, and dark-on-light contrast look clear on camera.
            </p>
          ) : (
            <ul data-testid="qr-check-warnings-list" className="space-y-2">
              {warnings.map((w) => (
                <li
                  key={w.code}
                  data-testid={`qr-check-warning-${w.code}`}
                  className="flex items-start gap-2.5 rounded-control border border-status-warning-border bg-status-warning-bg p-3 text-[12px] text-status-warning"
                >
                  <IconAlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-0.5">
                    <p className="font-semibold">{w.label}</p>
                    <p className="leading-relaxed">{w.message}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <p
          data-testid="qr-check-verifier-notice"
          className="rounded-control border border-border bg-surface-subtle px-3.5 py-2.5 text-[12px] font-semibold text-fg"
        >
          {evaluation.verifierNotice}
        </p>
      </CardContent>
    </Card>
  );
}
